import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { AbstractControl, FormsModule } from '@angular/forms';
import { KeyFilterModule } from 'primeng/keyfilter';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { FormBuilder, Validators, ReactiveFormsModule, FormGroup } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { DividerModule } from 'primeng/divider';
import { PasswordModule } from 'primeng/password';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { MessagesModule } from 'primeng/messages';
import { Message } from 'primeng/api';
import { numericOnlyInput, checkPhoneNumberValidity } from '../../../shared/utils/helpers'
import { LockerReservationService } from '../../../features/user/services/locker-reservation.service';
import { catchError, of, switchMap, tap } from 'rxjs';
import { DASHBOARD_HOME } from '../../../shared/constants/url';
import { Country } from '../../../shared/models/Countries.model';
import { CoreService } from '../../services/core.service';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    FormsModule,
    AutoCompleteModule,
    ReactiveFormsModule,
    InputTextModule,
    DropdownModule,
    InputNumberModule,
    DividerModule,
    RouterModule,
    PasswordModule,
    MessagesModule,
    KeyFilterModule,
    InputGroupModule,
    InputGroupAddonModule
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.sass'
})
export class LoginComponent {
  countryCodes!: Country[];
  formData: FormGroup = this.formBuilder.group({
    countryCode: [{name: 'Norway', Alpha2: 'NO', Alpha3: 'NOR', code: '+47'}],
    phoneNumber: [null, Validators.required],
    password: ['', Validators.required]
  });

  errorMessage = signal<Message[] | undefined>(undefined);
  isPhoneNumberValid = signal<boolean>(true);

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private lockerReservationService: LockerReservationService,
    private coreService$: CoreService,
    private route: ActivatedRoute
  ) { 
    this.coreService$.countries$.subscribe({
      next: (countries: Country[]) => {
        this.countryCodes = countries

        if (countries.length === 0) return;

        const countryCode = this.route.snapshot.queryParamMap.get('code');
        const phoneNumber = this.route.snapshot.queryParamMap.get('phoneNumber');

        if (!countryCode || !phoneNumber) return;

        const countryCodeParam = countries.find(country => country.code === countryCode);
        if (!countryCodeParam) return;

        this.formData.get('countryCode')?.setValue(countryCodeParam);
        this.formData.get('phoneNumber')?.setValue(phoneNumber);
      }
    })

  }

  get phoneNumber() {
    return this.formData.controls['phoneNumber'];
  }

  get countryCode() {
    return this.formData.controls['countryCode'].value;
  }

  get password() {
    return this.formData.controls['password'];
  }

  onSubmit() {
    const payload = { ...this.formData.value };
    let country = this.formData.value.countryCode.Alpha2
    payload.countryCode = payload.countryCode.code;
    payload.phoneNumber = String(payload.phoneNumber);

    // * Check if the phone number is valid based on country
    let isValidPhoneNumber = checkPhoneNumberValidity(payload.phoneNumber, country);
    this.isPhoneNumberValid.set(isValidPhoneNumber);

    if (this.formData.valid && this.isPhoneNumberValid()) {
      this.authService.login(payload).pipe(
        tap(() => {
          // Fire-and-forget preload; don't block navigation
          this.lockerReservationService.preloadUserKeyData().subscribe();
          const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
          this.router.navigateByUrl(returnUrl ?? DASHBOARD_HOME);
        }),
        catchError(error => {
          let message = error.error?.error || 'An unknown error occurred';
          this.setErrorMessage(message);
          localStorage.clear();

          return of(null); // Return an observable to complete the stream
        })
      ).subscribe();
    } else {
      const phoneNumber = this.formData.controls['phoneNumber'];
      const password = this.formData.controls['password'];
      this.checkFormValidity(phoneNumber);
      this.checkFormValidity(password);
    }
  }

  checkFormValidity(input: AbstractControl) {
    if (!input.value) {
      input.setErrors({ required: true });
      input.markAsTouched({ onlySelf: true });
      input.markAsDirty({ onlySelf: true });
    }
  }

  inputNumberOnly(event: KeyboardEvent): void {
    this.isPhoneNumberValid.set(true);
    // Allow '+' so users can type international numbers (e.g. +4755667788)
    if (event.key !== '+') {
      numericOnlyInput(event);
    }
  }

  onPhoneNumberPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const raw = event.clipboardData?.getData('text') ?? '';
    this.parseAndApplyPhoneInput(raw);
  }

  onPhoneNumberBlur(): void {
    const current = String(this.formData.get('phoneNumber')?.value ?? '');
    if (current.includes('+') || current.includes(' ')) {
      this.parseAndApplyPhoneInput(current);
    }
  }

  private parseAndApplyPhoneInput(raw: string): void {
    const cleaned = raw.replace(/\s+/g, '');
    let numberPart = cleaned;

    if (cleaned.startsWith('+')) {
      // Match longest country code first to avoid e.g. "+1" matching "+47"
      const sorted = [...this.countryCodes].sort(
        (a, b) => b.code.length - a.code.length
      );
      const match = sorted.find(c => cleaned.startsWith(c.code));
      if (match) {
        numberPart = cleaned.slice(match.code.length);
        this.formData.get('countryCode')?.setValue(match);
      }
    }

    numberPart = numberPart.replace(/\D/g, '');
    this.formData.get('phoneNumber')?.setValue(numberPart || null);
    this.isPhoneNumberValid.set(true);
  }

  setErrorMessage(message: string) {
    this.errorMessage.set([{ severity: 'error', summary: 'Error:', detail: message }]);
  }
}
